import { Link } from "react-router-dom";
import { ArrowRight, BedDouble, Check, MapPin, Ship } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteData } from "@/context/SiteDataContext";
import { waLink } from "@/lib/api";
import { IMG } from "@/lib/constants";
import { EnquiryDialog } from "./EnquiryDialog";
import { LogoMark } from "./Logo";
import { WhatsAppIcon } from "./WhatsAppIcon";

const SERVICES = [
  {
    id: "ferry", page: "/ferry-booking", icon: Ship, eyebrow: "Ferry Booking", title: "Book Ferries for Port Blair, Havelock & Neil Island", img: IMG.jetty,
    places: ["Port Blair – Havelock", "Havelock – Neil Island", "Neil Island – Port Blair"],
    points: ["Help choosing ferry timings and seat class", "One-way, return or full island-hopping routes", "Booked on its own, or together with your hotel and transfers"],
    cta: "Book Now", dialogTitle: "Andaman Ferry Booking",
    message: "Ferry booking enquiry - Route: , Travel date: , Passengers: ",
    wa: "Hi Ocean Wings Travel, I'd like to book an Andaman ferry. Route: , Date: , Passengers: ",
  },
  {
    id: "hotel", page: "/hotel-booking", icon: BedDouble, eyebrow: "Hotel Booking", title: "Book Hotels & Resorts across Andaman", img: IMG.luxury,
    places: ["Port Blair", "Swaraj Dweep (Havelock)", "Shaheed Dweep (Neil Island)"],
    points: ["Budget hotels to 5-star beach resorts", "Sea-view, beach-side and family-friendly rooms", "Hotel-only booking, or combined with ferries and transfers"],
    cta: "Book Now", dialogTitle: "Hotel Booking in Andaman",
    message: "Hotel booking enquiry - Island(s): , Check-in date: , Nights: , Rooms: ",
    wa: "Hi Ocean Wings Travel, I'd like to book a hotel in Andaman. Island(s): , Check-in: , Nights: , Guests: ",
  },
];

const ServiceCard = ({ s }) => {
  const { settings } = useSiteData();
  const Icon = s.icon;
  return (
    <article id={`${s.id}-booking`} className="scroll-mt-24 group rounded-3xl border border-slate-200 bg-white overflow-hidden flex flex-col transition-shadow duration-300 hover:shadow-xl" data-testid={`service-${s.id}`}>
      <div className="relative h-52 sm:h-60 overflow-hidden">
        <img src={s.img} alt={s.eyebrow} loading="lazy" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 via-[#0A192F]/10 to-transparent" />
        <span className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur pl-1 pr-3 py-1 shadow" data-testid={`service-${s.id}-brand`}>
          <LogoMark className="h-8 w-8" /><span className="text-xs font-bold text-navy">Ocean Wings Travel</span>
        </span>
        <p className="absolute bottom-4 left-5 right-5 flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-aqua"><Icon className="h-4 w-4" />{s.eyebrow}</p>
      </div>
      <div className="p-6 sm:p-8 flex flex-col flex-1">
        <h3 className="font-display text-2xl sm:text-3xl text-navy font-semibold leading-tight"><Link to={s.page} className="hover:text-ocean transition-colors" data-testid={`service-${s.id}-page-link`}>{s.title}</Link></h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {s.places.map((p) => <span key={p} className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-ocean"><MapPin className="h-3 w-3" />{p}</span>)}
        </div>
        <ul className="mt-5 space-y-2.5 flex-1">
          {s.points.map((p) => <li key={p} className="flex gap-2.5 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />{p}</li>)}
        </ul>
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <EnquiryDialog defaultMessage={s.message} title={s.dialogTitle} tripType={s.id === "ferry" ? "Ferry" : "Hotel"}
            trigger={<Button className="h-12 px-6 rounded-full bg-ocean hover:bg-navy font-semibold" data-testid={`service-${s.id}-cta`}>{s.cta} <ArrowRight className="ml-1 h-4 w-4" /></Button>} />
          <a href={waLink(settings.whatsapp, s.wa)} target="_blank" rel="noreferrer" data-testid={`service-${s.id}-whatsapp`}
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-emerald-200 text-emerald-700 font-semibold hover:bg-emerald-50 transition-colors">
            <WhatsAppIcon className="h-4 w-4" /> Enquire on WhatsApp
          </a>
        </div>
      </div>
    </article>
  );
};

export const TravelServices = () => (
  <section id="booking-services" className="container-x pt-20 sm:pt-24 scroll-mt-24" data-testid="home-travel-services">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
      <div>
        <p className="eyebrow">Ferry &amp; hotel booking</p>
        <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy font-semibold">Book ferries and hotels with us</h2>
        <p className="mt-3 text-slate-600 max-w-xl">Not looking for a full package? Ocean Wings Travel can book your inter-island ferry or hotel on its own.</p>
      </div>
      <Link to="/ferry-booking" className="text-sm font-semibold text-ocean hover:text-navy inline-flex items-center gap-1" data-testid="services-contact-link">Ferry routes &amp; tips <ArrowRight className="h-4 w-4" /></Link>
    </div>
    <div className="mt-10 grid gap-6 lg:grid-cols-2">
      {SERVICES.map((s) => <ServiceCard key={s.id} s={s} />)}
    </div>
  </section>
);
